import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDocumentTypeIdByCode } from '@/features/purchase-order/hooks/use-document-type-id-by-code';
import {
  type PurchaseOrderDocumentFormValues,
  purchaseOrderDocumentFormSchema,
} from '@/features/purchase-order/schema/purchase-order-document-form-schema';
import { useFileDropZone } from '@/hooks/use-file-drop-zone';
import { useUploadPurchaseOrderDocument } from '@/services/apis/mutations/purchase-orders/use-upload-purchase-order-document';

type UsePurchaseOrderDocumentFormParams = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  purchaseOrderId: string;
  fixedDocumentTypeCode?: string;
};

type PurchaseOrderDocumentEmailPayload = {
  recipients: string[];
  message: string;
};

const defaultValues: PurchaseOrderDocumentFormValues = {
  documentTypeId: '',
  file: undefined,
};

export function usePurchaseOrderDocumentForm(
  {
    open,
    onOpenChange,
    purchaseOrderId,
    fixedDocumentTypeCode,
  }: UsePurchaseOrderDocumentFormParams,
  emailPayload?: PurchaseOrderDocumentEmailPayload,
) {
  const { t } = useTranslation('purchaseOrder');
  const { t: tCommon } = useTranslation('common');
  const { mutateAsync: uploadDocument, isPending: isUploading } =
    useUploadPurchaseOrderDocument();

  const requireDocumentType = !fixedDocumentTypeCode;
  const documentTypeId = useDocumentTypeIdByCode(
    fixedDocumentTypeCode ?? 'other',
  );

  const resolver = useMemo(
    () =>
      zodResolver(
        purchaseOrderDocumentFormSchema({
          requireDocumentType,
        }),
      ),
    [requireDocumentType],
  );

  const { control, formState, handleSubmit, reset, setValue, watch } =
    useForm<PurchaseOrderDocumentFormValues>({
      defaultValues,
      resolver,
    });

  const setSelectedFile = useCallback(
    (file: File) => {
      setValue('file', file, { shouldDirty: true, shouldValidate: true });
    },
    [setValue],
  );

  const {
    dropZoneClassName,
    fileInputRef,
    handleDropZoneDragEnter,
    handleDropZoneDragLeave,
    handleDropZoneDragOver,
    handleDropZoneDrop,
    onFileInputChange,
    openFilePicker,
  } = useFileDropZone({
    open,
    onFileSelected: setSelectedFile,
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [open, reset, fileInputRef]);

  useEffect(() => {
    if (!fixedDocumentTypeCode) {
      return;
    }

    setValue('documentTypeId', documentTypeId ?? '');
  }, [fixedDocumentTypeCode, documentTypeId, setValue]);

  const onSubmit = useCallback(
    async (data: PurchaseOrderDocumentFormValues) => {
      if (!data.file) {
        return;
      }

      const resolvedTypeId = Number.parseInt(documentTypeId ?? '', 10);

      if (Number.isNaN(resolvedTypeId)) {
        toast.error(tCommon('error'), {
          description: t('documentsUploadError'),
        });
        return;
      }

      try {
        await uploadDocument({
          file: data.file,
          message: emailPayload?.message,
          purchaseOrderId,
          recipients: emailPayload?.recipients,
          typeId: resolvedTypeId,
        });
        toast.success(t('documentsUploadSuccess'));
        onOpenChange(false);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 409) {
          toast.error(t('documentsUploadDuplicate'));
        } else {
          toast.error(tCommon('error'), {
            description: t('documentsUploadError'),
          });
        }
      }
    },
    [
      documentTypeId,
      emailPayload?.message,
      emailPayload?.recipients,
      onOpenChange,
      purchaseOrderId,
      t,
      tCommon,
      uploadDocument,
    ],
  );

  const handleCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const selectedFile = watch('file');
  const isSubmitting = formState.isSubmitting || isUploading;

  return {
    control,
    dropZoneClassName,
    fileInputRef,
    formState,
    handleCancel,
    handleDropZoneDragEnter,
    handleDropZoneDragLeave,
    handleDropZoneDragOver,
    handleDropZoneDrop,
    handleSubmit,
    isSubmitting,
    onFileInputChange,
    onSubmit,
    openFilePicker,
    selectedFile,
    showDocumentTypeField: requireDocumentType,
  };
}
